import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AuthImage } from "@/components/AuthImage";
import { api } from "@/lib/api";
import { PRICING } from "@/data/categories";
import { Heart, Warning, Sparkle } from "@phosphor-icons/react";

export function RecapDialog({ open, onOpenChange, memories = [], stats, daysLeft }) {
  const navigate = useNavigate();
  const [insight, setInsight] = useState(null);

  useEffect(() => {
    if (open) api.get("/insights").then((r) => setInsight(r.data)).catch(() => {});
  }, [open]);

  const withPhotos = memories.filter((m) => m.photo_path).slice(0, 4);
  const thumbs = (withPhotos.length ? withPhotos : memories.slice(0, 4));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="recap-dialog"
        className="max-h-[92vh] overflow-y-auto bg-[#FDFBF7] border-[#4A4540]/15 sm:max-w-lg"
      >
        <DialogTitle className="sr-only">Everything you've created</DialogTitle>
        <DialogDescription className="sr-only">
          A recap of the memories you've gathered during your free trial, with an option to keep them forever.
        </DialogDescription>

        <p className="font-handwriting text-3xl text-[#B08D57]">look how far you've come</p>
        <h2 className="font-serif text-4xl leading-tight text-[#2C2A28]">
          Everything you've gathered
        </h2>

        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-serif text-5xl text-[#2C2A28]">{stats?.total ?? memories.length}</span>
          <span className="text-[#8A7F76]">
            {(stats?.total ?? memories.length) === 1 ? "memory kept so far" : "memories kept so far"}
          </span>
        </div>

        {thumbs.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-3">
            {thumbs.map((m, i) => (
              <div
                key={m.id}
                className={`w-24 bg-white p-1.5 pb-4 shadow-md ${i % 2 ? "rotate-2" : "-rotate-2"}`}
              >
                {m.photo_path ? (
                  <AuthImage path={m.photo_path} alt={m.title} className="aspect-square w-full object-cover" />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center bg-[#DDA77B]/25">
                    <span className="font-serif text-2xl text-[#B08D57]">{m.title?.[0]?.toUpperCase() || "?"}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {insight?.portrait_line && (
          <div className="mt-3 rounded-lg border border-[#7EA193]/30 bg-[#EAF1EE] p-4">
            <div className="flex items-center gap-1.5 text-[#4A6B5D]">
              <Sparkle size={16} weight="duotone" />
              <span className="text-xs font-medium uppercase tracking-wide">What we've already seen in you</span>
            </div>
            <p className="mt-1 font-handwriting text-2xl leading-snug text-[#2C2A28]">{insight.portrait_line}</p>
            {insight.growth_highlight && (
              <p className="mt-1 text-sm text-[#4A6B5D]">{insight.growth_highlight}</p>
            )}
          </div>
        )}

        <div className="mt-3 flex items-start gap-3 rounded-lg border border-[#B08D57]/40 bg-[#F7E3DE] p-4">
          <Warning size={20} weight="duotone" className="mt-0.5 shrink-0 text-[#B08D57]" />
          <p className="text-sm text-[#7A3D34]">
            {daysLeft > 0
              ? `Your free trial ends ${daysLeft === 1 ? "tomorrow" : `in ${daysLeft} days`}.`
              : "Your free trial ends today."}{" "}
            Unless you join, <strong>all of this will be permanently deleted</strong> — and
            we'll never charge you a cent if you don't.
          </p>
        </div>

        <Button
          data-testid="recap-keep-btn"
          onClick={() => navigate("/upgrade")}
          className="mt-4 w-full rounded-full bg-[#B08D57] py-6 text-base text-[#FDFBF7] hover:-translate-y-0.5 hover:bg-[#C19A6B]"
        >
          <Heart size={18} weight="duotone" className="mr-2" /> Keep it forever — {PRICING.currency}{PRICING.lifetime.price}
        </Button>
        <button
          data-testid="recap-later-btn"
          onClick={() => onOpenChange(false)}
          className="mt-1 w-full text-sm text-[#8A7F76] hover:text-[#B08D57]"
        >
          Maybe later
        </button>
      </DialogContent>
    </Dialog>
  );
}
